import React, { useContext, useEffect, useState } from 'react';
import { concat, each, flatten, sortBy, values } from 'lodash';
import { AccountContext } from '@/context/account.context';
import { Row, Col, Container, Card } from 'react-bootstrap';
import t from '@/utils/Language/Plan-translations';
import Accordion from 'react-bootstrap/Accordion';
import ResourceIcon from '@/components/Resources/ResourceIcon/ResourceIcon';
import Bookmarks from '@/utils/api/odas/bookmarks';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { tagHelpers } from '@/utils/helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classes from './PlanList.module.scss';
import UtilizationResourceCard from '@/components/Resources/ResourceCard/UtilizationResourceCard';
import intersection from 'lodash/intersection';
import differenceBy from 'lodash/differenceBy';
import unionBy from 'lodash/unionBy';
import map from 'lodash/map';
import Button from '../Button/Button';
import { viewportContext } from '@/context/viewport.context';
import Link from '@/helpers/Link';

const staticIcons = {
  Completed: ['fas', 'check'],
  Archived: ['fas', 'archive']
};

const AccessEventCollection = ({ accessEventCollection, as, className = '', defaultActiveKey, showCounts = false, ...aecProps }) => {
  return (
    <Accordion defaultActiveKey={defaultActiveKey} flush alwaysOpen className={className}>
      {accessEventCollection &&
        accessEventCollection.index.map((group, index) => {
          let groupClass = typeof group === 'string' ? group.toLowerCase() : 'rocket';
          return (
            <Accordion.Item key={groupClass} eventKey={index}>
              <Accordion.Header>
                <div className={`${classes.colorBorder} ${classes[tagHelpers.areaToSlug(group)]}`}></div>
                <div className={classes.panelTitleContent}>
                  {['Completed', 'Archived'].indexOf(group) >= 0 ? (
                    <FontAwesomeIcon icon={staticIcons[group]} className={`${classes[group.toLowerCase()]} ${classes.panelIcon}`} />
                  ) : (
                    <ResourceIcon
                      className={`${classes.panelIcon} ${classes.resourceIcon} ${classes[groupClass]}`}
                      resource={accessEventCollection.accessEvents[group][0].resource}
                    />
                  )}
                  <span data-cy={`accordion-title-index-${index}`} className={classes.panelTitle}>
                    {t(group)}
                  </span>

                  {showCounts && (
                    <>
                      <br />
                      <span className={classes.panelSubtitle}>
                        {t(accessEventCollection.accessEvents[group].length === 1 ? 'saved N resource' : 'saved N resources', {
                          count: accessEventCollection.accessEvents[group].length
                        })}
                      </span>
                    </>
                  )}
                </div>
              </Accordion.Header>
              <Accordion.Body index={index}>
                {accessEventCollection.accessEvents[group].map((ae, index) => {
                  return <UtilizationResourceCard key={ae.id} accessEvent={ae} />;
                })}
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
    </Accordion>
  );
};

/**
 * @showTitle (true | false) If true, display a "My Plan" title
 * @showFullPlan (true | false) If true, show Completed and Archived sections
 * @containerBootstrapClass (String) a css class to apply to the Bootstrap container of this component
 */
const PlanList = ({ showTitle = true, showFullPlan = false, containerBootstrapClass = '', ...props }) => {
  const account = useContext(AccountContext);
  const [activeAccessEvents, setActiveAccessEvents] = useState({ accessEvents: {}, index: [] });
  const [inactiveAccessEvents, setInactiveAccessEvents] = useState({ accessEvents: {}, index: [] });
  const [loading, setLoading] = useState({ active: true, inactive: true });
  const { isMobile, isMobileOrTablet } = useContext(viewportContext);

  useEffect(() => {
    if (
      !account.state.isLoggedIn ||
      (typeof account.accessEvents?.hasAccessEvents !== 'undefined' && typeof account.bookmarks?.planBookmarks !== 'undefined')
    ) {
      setLoading((prevState) => ({ ...prevState, active: false }));
    } else {
      account.fetchBookmarksAndAccessEvents();
      setLoading((prevState) => ({ ...prevState, active: true }));
    }
  }, [account.state.isLoggedIn, account.accessEvents?.hasAccessEvents, account.bookmarks?.planBookmarks]);

  useEffect(() => {
    let mounted = true;
    const organizePlan = async () => {
      let activeGroup = {};
      // The index keeps track of the order of the groups. Keep that order in place while component is mounted
      // Otherwise, the UX is a bit overhwleming with the groups of resource cards re-ordering when actions are taken
      let activeIndex = [...activeAccessEvents.index];
      let organizationBookmarks;
      let combinedBookmarks;

      // avoid processing plan list data until all data is ready
      if (loadingReady() && mounted) {
        // check for current orgs in activeAccessEvents list
        if (typeof activeAccessEvents.accessEvents.Organization !== 'undefined') {
          // to support the ability to 'undo' deleting Organizations from PlanList, determine if there's
          // a difference between the list of bookmarks in state vs the list from the accountContext (source of truth from the db)
          // keep deleted bookmarks in memory here through state to provide the undo option in the PlanList until this component is unmounted
          let deletedBookmarks = map(
            differenceBy(activeAccessEvents.accessEvents.Organization, account.bookmarks.planBookmarks, 'fetchable_id'),
            'fetchable_id'
          );
          // order is important here
          combinedBookmarks = unionBy(activeAccessEvents.accessEvents.Organization, account.bookmarks.planBookmarks, 'fetchable_id').map(
            (bookmark) => {
              if (deletedBookmarks.indexOf(bookmark.fetchable_id) >= 0) {
                bookmark.deleted = true;
              } else {
                delete bookmark.deleted;
              }
              return bookmark;
            }
          );
        } else {
          combinedBookmarks = account.bookmarks.planBookmarks;
        }

        organizationBookmarks = sortBy(combinedBookmarks, (bookmark) => bookmark.updated_at).reverse();

        // sort the Access Event categories by updated_at descendingly
        each(getActiveAccessEvents(), (ae) => {
          if (ae) {
            // not all resources have 'area' defined so determine it from the first tags
            const [area] = tagHelpers.areaAndCategoryFromTag(ae.resource.tags[0]);
            activeGroup[area] = activeGroup[area] || [];
            activeGroup[area].push(ae);
            // check where to place the Organization category in the list
            if (organizationBookmarks.length && organizationBookmarks[0].updated_at > ae.updated_at && activeIndex.indexOf('Organization') === -1) {
              activeIndex.push('Organization');
            }
            // use an index array to specify the order since object keys have no guaranteed ordering
            if (activeIndex.indexOf(area) < 0) {
              activeIndex.push(area);
            }
          }
        });
        if (organizationBookmarks.length) {
          // fetch resources for the Bookmarks
          activeGroup['Organization'] = await Bookmarks.addResourceToBookmarks(organizationBookmarks);
          // if the Organization category wasn't added to the index in the above loop, add it here.
          if (activeIndex.indexOf('Organization') === -1) {
            activeIndex.push('Organization');
          }
        }
        // Resolve cases where a group previously in the index has since been removed
        // The main scenario this happens is when the last remaining AccessEvent inside a Tag Area section is removed after answering the utilization question.
        // It will then move to the Completed/Archived section and the Tag Area toggle section should no longer be displayed.
        activeIndex = intersection(activeIndex, Object.keys(activeGroup));
        setActiveAccessEvents({ accessEvents: activeGroup, index: [...activeIndex] });
        setLoading((prevState) => ({ ...prevState, active: false }));
      }
    };
    organizePlan();

    return () => {
      mounted = false;
    };
  }, [account.bookmarks?.planBookmarks, account.accessEvents?.planned, account.accessEvents?.accessed, account.accessEvents?.clrStatus]);

  useEffect(() => {
    let mounted = true;
    if (loadingReady() && mounted) {
      if (showFullPlan) {
        let inActiveGroup = {};
        let inActiveIndex = [];

        let completedAccessEvents = getCompletedAccessEvents();
        if (completedAccessEvents?.length) {
          inActiveGroup['Completed'] = completedAccessEvents;
          inActiveIndex.push('Completed');
        }
        let archivedAccessEvents = getArchivedAccessEvents();
        if (archivedAccessEvents?.length) {
          inActiveGroup['Archived'] = archivedAccessEvents;
          inActiveIndex.push('Archived');
        }
        setInactiveAccessEvents({ accessEvents: inActiveGroup, index: inActiveIndex });
      }
      setLoading((prevState) => ({ ...prevState, inactive: false }));
    }
    return () => {
      mounted = false;
    };
  }, [account.accessEvents?.utilized, account.accessEvents?.unsuccessful, account.accessEvents?.rejected]);

  const getResourceCount = () => {
    let resources = flatten(values(activeAccessEvents.accessEvents)).filter((ae) => {
      return !ae.deleted;
    });
    return typeof resources.length !== 'undefined' ? resources.length : 0;
  };

  const getActiveAccessEvents = () => {
    return concat(account.accessEvents.planned, account.accessEvents.accessed, account.accessEvents.clrStatus);
  };

  const getArchivedAccessEvents = () => {
    return concat(account.accessEvents.unsuccessful, account.accessEvents.rejected);
  };

  const getCompletedAccessEvents = () => {
    return account.accessEvents.utilized;
  };

  const hasPlannedItem = () => {
    return account.state.isLoggedIn && (account.accessEvents?.hasAccessEvents || account.bookmarks?.planBookmarks?.length);
  };

  const showArchivedOrCompletedAccessEvents = () => {
    return showFullPlan && inactiveAccessEvents.index.length > 0;
  };

  const loadingReady = () => {
    return typeof account.bookmarks?.planBookmarks !== 'undefined' && typeof account.accessEvents?.hasAccessEvents !== 'undefined';
  };

  const goToBrowseResources = () => {
    window.location.href = '/search';
  };

  return (
    <Container className={containerBootstrapClass}>
      {!!(hasPlannedItem() && !loading.active) && (
        <div className={`${classes.planList}`}>
          {showTitle && (
            <Row>
              <Col className="position-relative">
                {loading.active ? (
                  <Skeleton />
                ) : (
                  <div className="d-flex flex-column">
                    <div className={'position-relative p-0'}>
                      <Link href={'plan'} className={classes.myPlanBtn}>
                        <FontAwesomeIcon icon={['fas', 'heart']} /> &nbsp;
                        {t('My Plan')}
                      </Link>
                    </div>
                    <span className={classes.panelSubtitle}>{t('Saved Resources')}</span>
                  </div>
                )}
              </Col>
            </Row>
          )}
          <Row>
            <span
              className={classes.panelSubtitle}
              dangerouslySetInnerHTML={{
                __html: t(getResourceCount() === 1 ? 'You have resource to take action!' : 'You have resources to take action!', {
                  count: getResourceCount()
                })
              }}
            />
          </Row>
          <Row className="my-2">
            <Col>
              {loading.active ? (
                <Skeleton height={90} />
              ) : (
                <AccessEventCollection
                  data-cy="text-resource-count"
                  defaultActiveKey={[0]}
                  accessEventCollection={activeAccessEvents}
                  showCounts={true}
                />
              )}
            </Col>
          </Row>
          {showArchivedOrCompletedAccessEvents() && (
            <Row className="my-4">
              <Col>
                <hr className="my-4" />
              </Col>
            </Row>
          )}
          {showArchivedOrCompletedAccessEvents() && (
            <Row>
              <Col>
                {loading.inactive ? (
                  <Skeleton height={60} />
                ) : (
                  <AccessEventCollection accessEventCollection={inactiveAccessEvents} className={classes.singleLineTitle} />
                )}
              </Col>
            </Row>
          )}
        </div>
      )}
      {!hasPlannedItem() && (
        <Row>
          <Col>
            {loading.active && (
              <>
                <Skeleton />
                <Skeleton height={90} />
                <Skeleton height={60} />
              </>
            )}
            {!loading.active && (
              <Card className={'p-3'}>
                <h5>{t('Save and keep up with important resources with My Plan')}</h5>
                <p>
                  {t('Want to keep track of important resources? Find a resource on One Degree and use the')}
                  &nbsp;
                  <FontAwesomeIcon icon={['far', 'heart']} />
                  &nbsp;
                  {t('Save button to automatically save it to My Plan.')}
                </p>
                <div className={'text-center'}>
                  <Button
                    dataCy="button-browse"
                    type="link"
                    icon={{ style: 'fas', font: 'search' }}
                    styles={{ width: isMobile || isMobileOrTablet ? 'full' : '' }}
                    text={t('Click here to find resources')}
                    onButtonClick={() => goToBrowseResources()}
                  />
                </div>
              </Card>
            )}
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default PlanList;
